/**
 * ToggleSwitch
 * iOS CupertinoSwitch-style toggle control.
 * Flutter source: CupertinoSwitch in setting_toggle_item.dart
 */

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

interface ToggleSwitchProps {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  /**
   * Optional `data-qa-*` set for the toggle `<button>` so the qa-tester can
   * locate it. To expose the toggle state, call-sites set
   * `value: value ? 'on' : 'off'`.
   */
  qa?: QaAttrOptions;
}

export const ToggleSwitch = ({
  value,
  onChange,
  disabled = false,
  qa,
}: ToggleSwitchProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const thumbPadding = responsive.toggle.thumbPadding;
  const trackWidth = responsive.icon.xlarge;
  const trackHeight = trackWidth * 0.6;
  const thumbSize = trackHeight - thumbPadding * 2;
  const thumbOffset = value ? trackWidth - thumbSize - thumbPadding : thumbPadding;

  const transition = `${ANIMATION_CONSTANTS.duration.fast}ms ${ANIMATION_CONSTANTS.easing.ease}`;

  return (
    <button
      {...(qa ? qaAttr(qa) : {})}
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      style={{
        position: 'relative',
        width: trackWidth,
        height: trackHeight,
        borderRadius: trackHeight / 2,
        backgroundColor: value ? theme.toggleActive : theme.toggleInactive,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: `background-color ${transition}`,
        padding: 0,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: thumbPadding,
          left: thumbOffset,
          width: thumbSize,
          height: thumbSize,
          borderRadius: thumbSize / 2,
          backgroundColor: theme.white,
          transition: `left ${transition}`,
          boxShadow: `0 ${responsive.toggle.shadowOffset}px ${responsive.toggle.shadowBlur}px ${theme.shadow}`,
        }}
      />
    </button>
  );
};
